// Sidebar - Main navigation sidebar component for ContentOS 3
import React, { useEffect, useRef } from 'react';
import {
    LayoutDashboard,
    PenTool,
    Info,
    LogIn,
    User,
    Gem,
    FileText,
    History,
    MoreHorizontal,
    Flame,
    Wand2,
    Rss,
} from 'lucide-react';
import Avatar from 'boring-avatars';
import NavButton from '../common/NavButton';
import UserCenter from '../modals/UserCenter';

const Sidebar = ({ activeTab, setActiveTab, user, userPoints, isLoggedIn, setShowRecharge, setShowMenu, showMenu, handleLogout, onLogin }) => {
    const canManageRss = Boolean(user?.is_staff);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!showMenu) return;

        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showMenu, setShowMenu]);

    return (
        <div className="z-30 flex h-full w-[72px] flex-shrink-0 flex-col border-r border-[#E9ECF5] bg-[#F8FAFC] shadow-sm">
            <div
                className="group flex cursor-pointer flex-col items-center justify-center py-6"
                onClick={() => setActiveTab('create')}
                title="返回创作"
            >
                <div className="relative mb-2 flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-[#2563EB] to-[#3B82F6] text-xl font-black text-white shadow-lg transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6">
                    C
                </div>
                <div className="flex flex-col items-center leading-none">
                    <span className="font-sans text-[11px] font-black tracking-wider text-[#4A4F63]">ContentOS</span>
                </div>
            </div>

            <div className="mx-auto mb-2 h-0.5 w-10 bg-gradient-to-r from-transparent via-[#BFDBFE] to-transparent" />

            <nav className="flex flex-1 flex-col items-center space-y-4 px-2 py-2">
                <NavButton
                    active={activeTab === 'create'}
                    onClick={() => setActiveTab('create')}
                    icon={PenTool}
                    label="创作"
                />
                <NavButton
                    active={activeTab === 'hotspot'}
                    onClick={() => setActiveTab('hotspot')}
                    icon={Flame}
                    label="热点"
                />
                <NavButton
                    active={activeTab === 'tasks'}
                    onClick={() => setActiveTab('tasks')}
                    icon={LayoutDashboard}
                    label="历史"
                />

                {/* <NavButton
                    active={activeTab === 'skills'}
                    onClick={() => setActiveTab('skills')}
                    icon={Wand2}
                    label="模板"
                /> */}
                {canManageRss && (
                    <NavButton
                        active={activeTab === 'rss'}
                        onClick={() => setActiveTab('rss')}
                        icon={Rss}
                        label="订阅"
                    />
                )}

                <NavButton
                    active={activeTab === 'about'}
                    onClick={() => setActiveTab('about')}
                    icon={Info}
                    label="关于"
                />
            </nav>

            <div className="flex flex-col items-center gap-4 p-2 pb-6">
                {isLoggedIn && (
                    <div
                        onClick={() => setShowRecharge(true)}
                        className="group relative flex w-full cursor-pointer flex-col items-center justify-center overflow-hidden rounded-2xl border-2 border-[#BFDBFE] bg-[#EFF6FF] p-2 transition-all hover:border-[#2563EB] hover:bg-[#DBEAFE] active:scale-95"
                        title="点击充值"
                    >
                        <Gem className="mb-1 h-4 w-4 text-[#2563EB] transition-transform group-hover:scale-110" />
                        <span className="text-xs font-black text-[#2563EB]">{userPoints || 0}</span>
                        <span className="mt-0.5 rounded-full bg-white px-1.5 text-[9px] font-bold text-[#3B82F6] shadow-sm">VIP</span>
                    </div>
                )}

                <div className="relative" ref={menuRef}>
                    <button
                        onClick={() => {
                            if (isLoggedIn) {
                                setShowMenu(!showMenu);
                            } else {
                                onLogin();
                            }
                        }}
                        className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all ${isLoggedIn
                            ? 'border-[#BFDBFE] bg-white shadow-sm hover:border-[#2563EB] hover:shadow-md'
                            : 'border-[#BFDBFE] bg-[#EFF6FF] text-[#2563EB] hover:border-[#2563EB] hover:bg-[#DBEAFE] hover:shadow-md'
                            }`}
                        title={isLoggedIn ? '账户中心' : '登录'}
                    >
                        {isLoggedIn ? (
                            <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-white">
                                <Avatar
                                    size={32}
                                    name={String(user?.username || user?.id || '')}
                                    variant="beam"
                                    colors={['#2563EB', '#3B82F6', '#60A5FA', '#5A67D8', '#93C5FD']}
                                />
                            </div>
                        ) : <User className="h-5 w-5" />}
                    </button>

                    <UserCenter
                        isOpen={showMenu && isLoggedIn}
                        onClose={() => setShowMenu(false)}
                        onLogout={handleLogout}
                        onRecharge={() => {
                            setShowMenu(false);
                            setShowRecharge(true);
                        }}
                        onUpdateLog={() => {
                            setShowMenu(false);
                            setActiveTab('changelog');
                        }}
                        onSettings={() => {
                            setShowMenu(false);
                            setActiveTab('settings');
                        }}
                    />

                    {showMenu && !isLoggedIn && (
                        <div className="animate-in fade-in slide-in-from-left-2 absolute bottom-2 left-12 z-50 ml-2 w-48 rounded-xl border border-gray-200 bg-white py-2 shadow-xl">
                            <div className="mb-1 border-b border-gray-50 px-4 py-2">
                                <p className="text-xs font-bold text-gray-900">访客</p>
                            </div>
                            <button className="flex w-full items-center px-4 py-2 text-left text-xs text-gray-600 hover:bg-gray-50">
                                <FileText className="mr-2 h-3.5 w-3.5" /> 平台协议
                            </button>
                            <button
                                onClick={() => {
                                    setActiveTab('changelog');
                                    setShowMenu(false);
                                }}
                                className="flex w-full items-center px-4 py-2 text-left text-xs text-gray-600 hover:bg-gray-50"
                            >
                                <History className="mr-2 h-3.5 w-3.5" /> 更新日志
                            </button>
                            <div className="my-1 h-px bg-gray-100" />
                            <button onClick={onLogin} className="flex w-full items-center px-4 py-2 text-left text-xs text-[#2563EB] hover:bg-[#EFF6FF]">
                                <LogIn className="mr-2 h-3.5 w-3.5" /> 立即登录
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Sidebar;
